import type {
  ChangeRequestItem,
  ChangeRequestStatus,
} from "@/services/APIs/PtsChangeRequestService";

export const CHANGE_REQUEST_PRIORITIES = ["High", "Medium", "Low"] as const;

export const CHANGE_REQUEST_STATUS_OPTIONS = [
  "Pending",
  "On Hold",
  "Reject",
  "Approved",
] as const;

export const RAISED_TO_CLIENT_STATUS_OPTIONS = [
  "Pending",
  "On Hold",
  "Reject",
  "Approved",
] as const;

export type NormalizedStatus =
  | "pending"
  | "on_hold"
  | "approved"
  | "reject"
  | "other";

export function normalizeStatus(
  status?: ChangeRequestStatus | null
): NormalizedStatus {
  const value = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");

  if (value === "approved") {
    return "approved";
  }

  if (value === "on hold") {
    return "on_hold";
  }

  if (value === "reject" || value === "rejected") {
    return "reject";
  }

  if (value === "pending" || value.includes("pending")) {
    return "pending";
  }

  return "other";
}

export function statusLabel(status?: ChangeRequestStatus | null): string {
  const normalized = normalizeStatus(status);
  switch (normalized) {
    case "pending":
      return "Pending";
    case "on_hold":
      return "On Hold";
    case "approved":
      return "Approved";
    case "reject":
      return "Reject";
    default:
      return status ? String(status) : "—";
  }
}

export function matchesStatusFilter(
  itemStatus: string | null | undefined,
  filterStatus: string | undefined
): boolean {
  if (!filterStatus) {
    return true;
  }
  return normalizeStatus(itemStatus) === normalizeStatus(filterStatus);
}

export function matchesRaisedToClientFilter(
  itemStatus: string | null | undefined,
  filterStatus: string | undefined
): boolean {
  if (!filterStatus) {
    return true;
  }
  return normalizeStatus(itemStatus) === normalizeStatus(filterStatus);
}

export function filterChangeRequests(
  items: ChangeRequestItem[],
  filters: {
    projectId?: string;
    status?: string;
    raisedToClientStatus?: string;
  }
): ChangeRequestItem[] {
  return items.filter(item => {
    if (
      filters.projectId &&
      String(item.project) !== String(filters.projectId)
    ) {
      return false;
    }
    if (!matchesStatusFilter(item.status, filters.status)) {
      return false;
    }
    if (
      !matchesRaisedToClientFilter(
        item.raised_to_client_status,
        filters.raisedToClientStatus
      )
    ) {
      return false;
    }
    return true;
  });
}

export function statusBadgeClass(status?: ChangeRequestStatus | null): string {
  const normalized = normalizeStatus(status);

  switch (normalized) {
    case "pending":
      return "bg-amber-50 text-amber-700 border-amber-200";

    case "on_hold":
      return "bg-sky-50 text-sky-700 border-sky-200";

    case "approved":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";

    case "reject":
      return "bg-rose-50 text-rose-700 border-rose-200";

    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
}

export function formatHours(value?: number | string | null): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return String(value);
  }
  return `${num} hrs`;
}

export function formatInr(value?: number | string | null): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return String(value);
  }
  return `₹${num.toLocaleString("en-IN")}`;
}

export function formatDateYmd(value?: string | null): string {
  if (!value) {
    return "—";
  }
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const dd = String(parsed.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function isForwarded(item: ChangeRequestItem): boolean {
  if (item.forwarded_to_accountant || item.forwarded) {
    return true;
  }

  return normalizeStatus(item.status) === "approved";
}

export function resolveName(
  idOrName: string | undefined | null,
  lookup: Map<string, string>
): string {
  if (!idOrName) {
    return "—";
  }
  const key = String(idOrName);
  return lookup.get(key) ?? key;
}

export function getMonthBounds(year: number, monthIndex: number) {
  const start = new Date(year, monthIndex, 1);
  const end = new Date(year, monthIndex + 1, 0);
  const toYmd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: toYmd(start), end: toYmd(end) };
}

export function isInMonth(
  dateValue: string | undefined | null,
  year: number,
  monthIndex: number
): boolean {
  if (!dateValue) {
    return false;
  }
  const ymd = formatDateYmd(dateValue);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return false;
  }
  const [y, m] = ymd.split("-").map(Number);
  return y === year && m === monthIndex + 1;
}

export function monthLabel(year: number, monthIndex: number): string {
  const date = new Date(year, monthIndex, 1);
  return date.toLocaleString("en-US", { month: "long", year: "numeric" });
}

/** Plain text from Quill/HTML for validation and display. */
export function getPlainTextFromHtml(html: string): string {
  if (!html || typeof html !== "string") {
    return "";
  }
  const trimmed = html.trim();
  if (!trimmed) {
    return "";
  }
  if (typeof document !== "undefined") {
    const tmp = document.createElement("div");
    tmp.innerHTML = trimmed;
    return (tmp.textContent ?? "").replace(/\u00a0/g, " ").trim();
  }
  return trimmed
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\u00a0/g, " ")
    .trim();
}

export function monthName(monthIndex: number): string {
  return new Date(2000, monthIndex, 1).toLocaleString("en-US", {
    month: "long",
  });
}
